import { fetchPortalInstancesByIds, type PortalInstance } from './portalClient';

/**
 * Batches id lookups so a screen full of badges costs one request.
 *
 * A chart legend (PR #353) can carry 20 series and a chat response several markers.
 * React Query would happily hold 20 cache entries, but 20 round trips to Salesforce is
 * another matter — so ids requested during one render commit are collapsed into a single
 * `?id=a,b,c`. The flush is a microtask: React runs every mount effect in the same
 * macrotask, so all of a commit's queries land in one batch.
 */
export type InstanceDirectory = {
  load: (id: string) => Promise<PortalInstance | undefined>;
};

type Waiter = {
  resolve: (instance: PortalInstance | undefined) => void;
  reject: (error: unknown) => void;
};

export const createInstanceDirectory = (
  fetchByIds: (ids: string[]) => Promise<PortalInstance[]>,
): InstanceDirectory => {
  let pending = new Map<string, Waiter[]>();
  let isScheduled = false;

  const flush = async () => {
    const batch = pending;
    pending = new Map();
    isScheduled = false;

    const ids = [...batch.keys()];
    try {
      const instances = await fetchByIds(ids);
      const byId = new Map(instances.filter((it) => it.id).map((it) => [it.id as string, it]));
      for (const [id, waiters] of batch) {
        for (const waiter of waiters) waiter.resolve(byId.get(id));
      }
    } catch (error) {
      // A failed request is "we don't know", never "not in scope" — callers distinguish
      // the two, so a rejection must not be flattened into `undefined`.
      for (const waiters of batch.values()) {
        for (const waiter of waiters) waiter.reject(error);
      }
    }
  };

  return {
    load: (id) =>
      new Promise((resolve, reject) => {
        const waiters = pending.get(id) ?? [];
        waiters.push({ resolve, reject });
        pending.set(id, waiters);
        if (!isScheduled) {
          isScheduled = true;
          queueMicrotask(flush);
        }
      }),
  };
};

/**
 * One live directory, keyed by host + token. Keeping only the current key means a
 * rotated token can't accumulate stale directories, and every caller in a commit shares
 * the same batch — which is the whole point.
 */
let current: { key: string; directory: InstanceDirectory } | null = null;

export const getInstanceDirectory = (baseUrl: string, token: string): InstanceDirectory => {
  const key = `${baseUrl}|${token}`;
  if (current?.key !== key) {
    current = {
      key,
      directory: createInstanceDirectory((ids) => fetchPortalInstancesByIds({ baseUrl, token, ids })),
    };
  }
  return current.directory;
};

/** Test seam: drop the memoized directory between cases. */
export const resetInstanceDirectory = (): void => {
  current = null;
};
